import { useEffect, useState } from "react";
import { useHeaderContext } from "../../context/headerContext";
import { useNavigate } from "react-router-dom";
import TextInput from "../../components/TextInput";
import Button from "../../components/Button";
import { z } from "zod";
import * as Sentry from "@sentry/react";
import Dropdown from "../../components/Dropdown";
import SelectInput from "../../components/SelectInput";
import { useUserContext } from "../../context/userContext";
import { LogErrors } from "../../utils/LogErrors";
import fetchWithRefresh from "../../utils/fetchWithRefresh";
import { toast } from "react-toastify";

export default function AccountRedeemBand() {
  const { setTitle, setBackButton } = useHeaderContext();
  const { user, participants, logout, updateTokens } = useUserContext();
  const [ errors, setErrors ] = useState({});
  const [ bandTypes, setBandTypes ] = useState([]);
  const [ bandColor, setBandColor ] = useState([]);
  const [ selectedParticipant, setSelectedParticipant ] = useState(false);
  const [ selectedBandType, setSelectedBandType ] = useState(false);
  const [ previousAddress, setPreviousAddress ] = useState([]);
  const [ defaultParticipant, setDefaultParticipant ] = useState(null);
  const navigate = useNavigate();
  let period;

  switch (user?.subscription?.ReplacementBandPeriod){
    case 1 :
      period = "a year"; break;
    case 2 :
      period = "every two years"; break;
    case 3 :
      period = "every three years"; break;
  }

  const formSchema = z.object({
    // Section 1
    participantSelect: z.string().min(1, "Please select a participant"),
    bandTypeSelect: z.number().min(1, "Please select a band type"),
    bandColorSelect: z.string().min(1, "Please select a band colour"),

    // Section 2
    participantName: z.string().min(1, "Participant name is required"),
    iceNumber: z
      .string()
      .min(6, "Please enter a valid telephone number")
      .regex(/^[\d+()\-\s]+$/, "Invalid characters in telephone number"),
    primaryMedicalCondition: z.string().min(1, "Primary medical condition is required"),

    // Section 3
    address1: z.string().min(1, "Address line 1 is required"),
    address2: z.string().optional(),
    city: z.string().min(1, "City/Town is required"),
    county: z.string().min(1, "County is required"),
    postcode: z
      .string()
      .min(1, "Post code is required")
      .regex(/^[A-Za-z0-9\s-]+$/, "Invalid post code"),
  });

  const fetchAvailableBandTypes = async () => {
    return await Sentry.startSpan({ name: "Fetch Available Band Types", op: "http.request" }, async (span) => {
      try {
        const url = `${import.meta.env.VITE_API_URL}Cart/FetchBandAvailableTypes`;
        span.setAttribute("http.url", url);
        span.setAttribute("user_id", user?.id);
  
        const req = await fetchWithRefresh(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token}`,
          },
        }, logout, updateTokens, (message) => { toast.info(message); });
  
        if (req.status === 200) {
          const res = await req.json();
          setBandTypes(res);
        } else {
          Sentry.captureMessage(`Failed to fetch available band types: ${req.status}`, "warning");
          LogErrors(user, logout, updateTokens, "Could not fetch available band types.");
        }
  
        span.setStatus(req.ok ? "ok" : "error");
      } catch (error) {
        Sentry.captureException(error, { extra: { user_id: user?.id } });
        LogErrors(user, logout, updateTokens, "Error fetching available band types: " + error);
      }
    });
  };

  const fetchPreviousAddress = async () => {
    return await Sentry.startSpan({ name: "Fetch Recent Address", op: "http.request" }, async (span) => {
      try {
        const url = `${import.meta.env.VITE_API_URL}Cart/FetchLastDeliveryAddress?UserLoginId=${user?.id}`;
        span.setAttribute("http.url", url);
        span.setAttribute("user_id", user?.id);
  
        const req = await fetchWithRefresh(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token}`,
          },
        }, logout, updateTokens, (message) => { toast.info(message); });
  
        if (req.status === 200) {
          const res = await req.json();
          setPreviousAddress(res);
        } else {
          Sentry.captureMessage(`Failed to fetch recent address: ${req.status}`, "warning");
          LogErrors(user, logout, updateTokens, "Could not find recent address.");
        }
  
        span.setStatus(req.ok ? "ok" : "error");
      } catch (error) {
        Sentry.captureException(error, { extra: { user_id: user?.id } });
        LogErrors(user, logout, updateTokens, "Error fetching recent address: " + error);
      }
    });
  };

  const fetchBandColours = async () => {
    return await Sentry.startSpan({ name: "Fetch Band Colours for Venue", op: "http.request" }, async (span) => {
      try {
        const url = `${import.meta.env.VITE_API_URL}Venue/FetchBandColoursForVenue?OrgId=${selectedBandType}`;
        span.setAttribute("http.url", url);
        span.setAttribute("user_id", user?.id);
  
        const req = await fetchWithRefresh(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token}`,
          },
        }, logout, updateTokens, (message) => { toast.info(message); });
  
        if (req.status === 200) {
          const res = await req.json();
          setBandColor(res);
        } else {
          Sentry.captureMessage(`Fetch Band Colours for Venue: ${req.status}`, "warning");
          LogErrors(user, logout, updateTokens, "Could Not Fetch Band Colours for Venue");
        }
  
        span.setStatus(req.ok ? "ok" : "error");
      } catch (error) {
        Sentry.captureException(error, { extra: { user_id: user?.id } });
        LogErrors(user, logout, updateTokens, "Error Fetch Band Colours for Venue: " + error);
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    const fd = new FormData(e.target);
    const values = {
      // Section 1
      participantSelect: fd.get("participantSelect")?.toString() || "",
      bandTypeSelect: parseInt(fd.get("bandTypeSelect")) || 0,
      bandColorSelect: fd.get("bandColorSelect")?.toString() || "",
      // Section 2
      participantName: fd.get("participantName")?.toString() || "",
      iceNumber: fd.get("iceNumber")?.toString() || "",
      primaryMedicalCondition: fd.get("primaryMedicalCondition")?.toString() || "",
      // Section 3
      address1: fd.get("address1")?.toString() || "",
      address2: fd.get("address2")?.toString() || "",
      city: fd.get("city")?.toString() || "",
      county: fd.get("county")?.toString() || "",
      postcode: fd.get("postcode")?.toString() || "",
    };

    const parsed = formSchema.safeParse(values);

    if (!parsed.success) {
      const fieldErrors = {};
      for (const issue of parsed.error.issues) {
        fieldErrors[issue.path.join(".")] = issue.message;
      }
      setErrors(fieldErrors);
      toast.error("Please fix the errors before continuing.");
      return;
    }

    await Sentry.startSpan({name: "Update User Profile Picture", op: "http.request" },
      async (span) => {
        try {
          const req = await fetchWithRefresh(import.meta.env.VITE_API_URL + `Cart/RedeemFreeBand`, {
            method: "POST",
            body: JSON.stringify({
              "userLoginId": user?.id,
              "participantId": parsed?.data?.participantSelect,
              "orgId": parsed?.data?.bandTypeSelect,
              "bandColour": parsed?.data?.bandColorSelect,
              "participantName": parsed?.data?.participantName,
              "iceNumber": parsed?.data?.iceNumber,
              "primaryMedicalCondition": parsed?.data?.primaryMedicalCondition,
              "addressLine1": parsed?.data?.address1,
              "addressLine2": parsed?.data?.address2,
              "town": parsed?.data?.city,
              "county": parsed?.data?.county,
              "postCode": parsed?.data?.postcode,
              "country": "UK"
            }),
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
          }, logout, updateTokens, (message) => { toast.info(message); });

          // Set HTTP response status in the span
          span.setAttribute("http.status_code", req.status);

          if (req.status === 200) {
            navigate("/account/redeem-success");
          } else {
            span.setStatus("error");
            LogErrors(user, logout, updateTokens, "Free band could not be redeeemed");
            toast.error("Something went wrong redeeming your free band.");
          }
        } catch (error) {
          span.setStatus("error");
          Sentry.captureException(error);
          LogErrors(user, logout, updateTokens, error);
        } finally {
          span.end();
        }
      }
    );
  };

  useEffect(() => {
    setTitle("REDEEM BAND");
    setBackButton(true);
    fetchPreviousAddress();
    fetchAvailableBandTypes();
  }, [setTitle, setBackButton]);

  useEffect(() => {
    if(!selectedBandType) return;
    fetchBandColours();
  }, [selectedBandType])

  useEffect(() => {
    if(!selectedParticipant) return;
    const participant = participants.find(p => p.ParticipantId.toString() === selectedParticipant);
    setDefaultParticipant(participant);
  }, [selectedParticipant]);

  return (
    <div className="p-[20px]">
      <div className="mb-12">
        <h2>{user?.subscription?.Name} Plan</h2>
        <p>Your {user?.subscription?.Name} Subscription includes a free band once {period}</p>
      </div>

      <form id="bandDetailsForm" onSubmit={handleSubmit} className="flex flex-col gap-[17px] pb-[100px]">

        {/* ===== 1) Options (Participant / Band Type / Colour) ===== */}
        <Dropdown
          title="Band Options"
          isActive
        >
          <div className="flex flex-col gap-5">
            {/* Participant select */}
            <div className="flex flex-col gap-2">
              <SelectInput
                name="participantSelect"
                className="capitalize"
                options={participants?.map(participant => (
                  <option key={participant?.ParticipantId} value={participant?.ParticipantId}>
                    {participant?.ParticipantName}
                  </option>
                ))}
                label="Participant"
                placeholder="Select Participant"
                error={errors.participantSelect}
                onChange={(e) => setSelectedParticipant(e.target.value)}
              />
            </div>

            {/* Band Type */}
            <div className="flex flex-col gap-2">
              <SelectInput
                name="bandTypeSelect"
                className="capitalize"
                options={bandTypes?.map(venue => (
                  <option key={venue?.OrgID} value={venue?.OrgID}>
                    {venue?.OrgPartnerFriendlyName}
                  </option>
                ))}
                label="Band Type"
                placeholder="Select Band Type"
                onChange={(e) => setSelectedBandType(e.target.value)}
                error={errors.bandTypeSelect}
              />
            </div>

            {/* Band Colour */}
            <div className="flex flex-col gap-2">
              <SelectInput
                name="bandColorSelect"
                className="capitalize mb-1"
                disabled={selectedBandType ? false : true}
                options={bandColor?.map(color => (
                  <option key={color?.BandColour} value={color?.BandColour} className=" capitalize">
                    {color?.BandColour}
                  </option>
                ))}
                label="Band Colour"
                placeholder="Select Colour"
                error={errors.bandColorSelect}
              />
            </div>
          </div>
        </Dropdown>

        {/* ===== 2) Engraving ===== */}
        <Dropdown
          title="Engraving Details"
        >
          <div className="flex flex-col gap-5">
            <TextInput
              name="participantName"
              label="Participant Name"
              placeholder="Enter full name"
              error={errors.participantName}
              defaultValue={defaultParticipant?.ParticipantName}
              required
            />
            <TextInput
              type="tel"
              name="iceNumber"
              label="ICE Contact Telephone Number"
              placeholder="Telephone Number"
              error={errors.iceNumber}
              defaultValue={defaultParticipant?.PrimaryIceNumber}
              required
            />
            <TextInput
              name="primaryMedicalCondition"
              label="Primary Medical Condition"
              placeholder="Enter condition"
              error={errors.primaryMedicalCondition}
              defaultValue={defaultParticipant?.PrimaryMedicalCondition}
              required
            />
          </div>
        </Dropdown>

        {/* ===== 3) Shipping ===== */}
        <Dropdown
          title="Shipping Address"
        >
          <div className="flex flex-col gap-5">
            <TextInput
              name="address1"
              label="Address Line 1"
              required
              error={errors.address1}
              defaultValue={previousAddress?.ShippingAddressLine1}
            />

            <TextInput
              name="address2"
              label="Address Line 2 (optional)"
              error={errors.address2}
              defaultValue={previousAddress?.ShippingAddressLine2}
            />

            <TextInput 
              name="city" 
              label="City/Town" 
              required
              error={errors.city}
              defaultValue={previousAddress?.ShippingAddressCity}
            />

            <TextInput 
              name="county" 
              label="County" 
              required
              error={errors.county}
              defaultValue={previousAddress?.ShippingAddressCounty}
            />

            <TextInput 
              name="postcode" 
              label="Post Code" 
              placeholder="Post code" 
              required
              error={errors.postcode}
              defaultValue={previousAddress?.ShippingAddressPostalCode}
            />

          </div>
        </Dropdown>

        <div className="border-t border-dashed border-[#E3E3E3] mt-[40px] pt-[21px]">
          <Button variant="primary" className="w-full" submit form="bandDetailsForm">
            Confirm & Continue
          </Button>
        </div>
      </form>
    </div>
  );
}
